import { Download, FileSpreadsheet, FileText, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExportActions, type ExportFormat, type EntityType } from "@/lib/export";

interface ExportToolbarProps {
  entityType: EntityType;
  /** The full set of filtered data rows to export. */
  data: any[];
  /** Optional human-readable description of active filters for audit. */
  filterDescription?: string;
  /** Whether running in customer portal context. */
  isCustomerPortal?: boolean;
}

const FORMAT_META: Record<ExportFormat, { label: string; icon: React.ReactNode }> = {
  excel: { label: "Export to Excel", icon: <FileSpreadsheet className="h-4 w-4 mr-2" /> },
  csv: { label: "Export to CSV", icon: <FileText className="h-4 w-4 mr-2" /> },
  print: { label: "Print", icon: <Printer className="h-4 w-4 mr-2" /> },
};

export const ExportToolbar = ({
  entityType,
  data,
  filterDescription,
  isCustomerPortal,
}: ExportToolbarProps) => {
  const { availableFormats, doExport } = useExportActions({
    entityType,
    isCustomerPortal,
  });

  if (availableFormats.length === 0 || !data?.length) return null;

  const handleExport = (format: ExportFormat) => {
    doExport(format, data, filterDescription);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Download className="h-4 w-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium">
          All {data.length} filtered row{data.length !== 1 ? "s" : ""}
        </div>
        <DropdownMenuSeparator />
        {availableFormats.map((format) => (
          <DropdownMenuItem key={format} onClick={() => handleExport(format)}>
            {FORMAT_META[format].icon}
            {FORMAT_META[format].label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
